import React, { useState } from "react";

type ContactKind = "email" | "phone" | "address";

interface ContactEntry {
  id?: number | string | null;
  kind: ContactKind;
  value?: string | null;
  label?: string | null;
  preferred?: boolean;
}

interface AlternativeNameEntry {
  id?: number | string | null;
  name?: string | null;
}

interface PatientDetailsEditorProps {
  contacts?: ContactEntry[];
  alternativeNames?: AlternativeNameEntry[];
}

interface ContactFormRow {
  clientKey: string;
  id: string;
  kind: ContactKind;
  value: string;
  label: string;
  preferred: boolean;
}

interface AlternativeNameFormRow {
  clientKey: string;
  id: string;
  name: string;
}

const CONTACT_SECTIONS: Array<{
  kind: ContactKind;
  title: string;
  description: string;
  addLabel: string;
  valueLabel: string;
  valuePlaceholder: string;
}> = [
  {
    kind: "email",
    title: "Email addresses",
    description: "Add every address the patient actively uses and mark the preferred one.",
    addLabel: "Add email",
    valueLabel: "Email address",
    valuePlaceholder: "patient@example.com",
  },
  {
    kind: "phone",
    title: "Phone numbers",
    description: "Track mobile, home, work, or caregiver numbers in one place.",
    addLabel: "Add phone",
    valueLabel: "Phone number",
    valuePlaceholder: "(602) 555-0100",
  },
  {
    kind: "address",
    title: "Mailing addresses",
    description: "Keep current and prior mailing addresses when staff still needs them.",
    addLabel: "Add address",
    valueLabel: "Mailing address",
    valuePlaceholder: "123 Desert Way\nPhoenix, AZ 85001",
  },
];

let nextClientKey = 0;

function buildClientKey(prefix: string): string {
  nextClientKey += 1;
  return `${prefix}-${nextClientKey}`;
}

function buildContactRow(kind: ContactKind, entry: Partial<ContactEntry> = {}): ContactFormRow {
  return {
    clientKey: buildClientKey(kind),
    id: entry.id ? String(entry.id) : "",
    kind,
    value: entry.value?.toString() || "",
    label: entry.label?.toString() || "",
    preferred: Boolean(entry.preferred),
  };
}

function buildAlternativeNameRow(entry: Partial<AlternativeNameEntry> = {}): AlternativeNameFormRow {
  return {
    clientKey: buildClientKey("alternative-name"),
    id: entry.id ? String(entry.id) : "",
    name: entry.name?.toString() || "",
  };
}

function buildInitialContactRows(entries: ContactEntry[]): ContactFormRow[] {
  return CONTACT_SECTIONS.flatMap(({ kind }) => {
    const rows = entries
      .filter((entry) => entry.kind === kind)
      .map((entry) => buildContactRow(kind, entry));

    if (rows.length === 0) {
      return [ buildContactRow(kind, { preferred: true }) ];
    }

    if (!rows.some((row) => row.preferred)) {
      rows[0].preferred = true;
    }

    return rows;
  });
}

function inputTypeFor(kind: ContactKind): string {
  if (kind === "email") return "email";
  if (kind === "phone") return "tel";

  return "text";
}

export default function PatientDetailsEditor({
  contacts: initialContacts = [],
  alternativeNames: initialAlternativeNames = [],
}: PatientDetailsEditorProps) {
  const [contacts, setContacts] = useState<ContactFormRow[]>(() => buildInitialContactRows(initialContacts));
  const [alternativeNames, setAlternativeNames] = useState<AlternativeNameFormRow[]>(
    () => initialAlternativeNames.map((entry) => buildAlternativeNameRow(entry))
  );

  const addContactRow = (kind: ContactKind) => {
    setContacts((previous) => {
      const nextRow = buildContactRow(kind, { preferred: previous.every((row) => row.kind !== kind || !row.preferred) });
      const lastIndex = previous.reduce((foundIndex, row, index) => (row.kind === kind ? index : foundIndex), -1);

      if (lastIndex < 0) return [ ...previous, nextRow ];

      const nextRows = [ ...previous ];
      nextRows.splice(lastIndex + 1, 0, nextRow);
      return nextRows;
    });
  };

  const updateContactRow = (clientKey: string, field: "value" | "label", value: string) => {
    setContacts((previous) =>
      previous.map((row) => (row.clientKey === clientKey ? { ...row, [field]: value } : row))
    );
  };

  const setPreferredContact = (kind: ContactKind, clientKey: string) => {
    setContacts((previous) =>
      previous.map((row) => {
        if (row.kind !== kind) return row;
        return { ...row, preferred: row.clientKey === clientKey };
      })
    );
  };

  const removeContactRow = (kind: ContactKind, clientKey: string) => {
    setContacts((previous) => {
      const nextRows = previous.filter((row) => row.clientKey !== clientKey);
      const sameKindRows = nextRows.filter((row) => row.kind === kind);

      if (sameKindRows.length === 0) {
        return [ ...nextRows, buildContactRow(kind, { preferred: true }) ].sort(
          (left, right) =>
            CONTACT_SECTIONS.findIndex((section) => section.kind === left.kind) -
            CONTACT_SECTIONS.findIndex((section) => section.kind === right.kind)
        );
      }

      if (!sameKindRows.some((row) => row.preferred)) {
        const firstRowKey = sameKindRows[0].clientKey;
        return nextRows.map((row) => (row.clientKey === firstRowKey ? { ...row, preferred: true } : row));
      }

      return nextRows;
    });
  };

  const addAlternativeName = () => {
    setAlternativeNames((previous) => [ ...previous, buildAlternativeNameRow() ]);
  };

  const updateAlternativeName = (clientKey: string, value: string) => {
    setAlternativeNames((previous) =>
      previous.map((row) => (row.clientKey === clientKey ? { ...row, name: value } : row))
    );
  };

  const removeAlternativeName = (clientKey: string) => {
    setAlternativeNames((previous) => previous.filter((row) => row.clientKey !== clientKey));
  };

  return (
    <div className="space-y-6">
      <section className="app-surface p-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-950 dark:text-white">Contact info</h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Keep every reachable email, phone, and mailing address on the chart. Preferred values drive the primary
            contact shown elsewhere in the app.
          </p>
        </div>

        <div className="mt-4 space-y-5">
          {CONTACT_SECTIONS.map((section) => {
            const sectionRows = contacts.filter((row) => row.kind === section.kind);
            const compactRow = section.kind !== "address";

            return (
              <div key={section.kind}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-950 dark:text-white">{section.title}</h3>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{section.description}</p>
                  </div>
                  <button type="button" className="app-button-secondary" onClick={() => addContactRow(section.kind)}>
                    {section.addLabel}
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {sectionRows.map((row) => (
                    <article key={row.clientKey} className="rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950/70">
                      <div className={compactRow ? "grid gap-3 lg:grid-cols-[minmax(0,1.8fr),14rem,auto,auto] lg:items-center" : "grid gap-3 lg:grid-cols-[minmax(0,1.8fr),14rem,auto,auto] lg:items-end"}>
                        <div className="min-w-0">
                          {compactRow ? (
                            <>
                              <label className="sr-only">{section.valueLabel}</label>
                              <input
                                type={inputTypeFor(section.kind)}
                                value={row.value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  updateContactRow(row.clientKey, "value", event.target.value)
                                }
                                className="app-input"
                                placeholder={section.valuePlaceholder}
                              />
                            </>
                          ) : (
                            <>
                              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                {section.valueLabel}
                              </label>
                              <textarea
                                value={row.value}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                  updateContactRow(row.clientKey, "value", event.target.value)
                                }
                                rows={4}
                                className="app-input mt-1 min-h-28"
                                placeholder={section.valuePlaceholder}
                              />
                            </>
                          )}
                        </div>

                        <div className="min-w-0">
                          {compactRow ? (
                            <>
                              <label className="sr-only">Label</label>
                              <input
                                type="text"
                                value={row.label}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  updateContactRow(row.clientKey, "label", event.target.value)
                                }
                                className="app-input"
                                placeholder="Label"
                              />
                            </>
                          ) : (
                            <>
                              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                Label
                              </label>
                              <input
                                type="text"
                                value={row.label}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  updateContactRow(row.clientKey, "label", event.target.value)
                                }
                                className="app-input mt-1"
                                placeholder="Home, seasonal..."
                              />
                            </>
                          )}
                        </div>

                        <label className={compactRow ? "flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300" : "flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300 lg:pb-2"}>
                          <input
                            type="radio"
                            name={`preferred-contact-${section.kind}`}
                            checked={row.preferred}
                            onChange={() => setPreferredContact(section.kind, row.clientKey)}
                          />
                          Preferred
                        </label>

                        <div className={compactRow ? undefined : "lg:pb-1"}>
                          <button
                            type="button"
                            className="app-button-secondary"
                            onClick={() => removeContactRow(section.kind, row.clientKey)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <input type="hidden" name="patient[contact_entries][][id]" value={row.id} />
                      <input type="hidden" name="patient[contact_entries][][kind]" value={row.kind} />
                      <input type="hidden" name="patient[contact_entries][][value]" value={row.value} />
                      <input type="hidden" name="patient[contact_entries][][label]" value={row.label} />
                      <input type="hidden" name="patient[contact_entries][][preferred]" value={row.preferred ? "1" : "0"} />
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="app-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-neutral-950 dark:text-white">Alternative names</h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Capture maiden names, nicknames, or prior chart names so staff can still find the patient.
            </p>
          </div>
          <button type="button" className="app-button-secondary" onClick={addAlternativeName}>
            Add alternative name
          </button>
        </div>

        {alternativeNames.length > 0 ? (
          <div className="mt-4 space-y-3">
            {alternativeNames.map((row) => (
              <article
                key={row.clientKey}
                className="rounded-lg border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-950/40"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Alternative name</label>
                    <input
                      type="text"
                      value={row.name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateAlternativeName(row.clientKey, event.target.value)
                      }
                      className="app-input mt-1"
                      placeholder="Previous legal name, nickname, maiden name..."
                    />
                  </div>

                  <button type="button" className="app-button-secondary" onClick={() => removeAlternativeName(row.clientKey)}>
                    Remove
                  </button>
                </div>

                <input type="hidden" name="patient[alternative_name_entries][][id]" value={row.id} />
                <input type="hidden" name="patient[alternative_name_entries][][name]" value={row.name} />
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-dashed border-neutral-300/80 bg-white/70 p-4 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-950/40 dark:text-neutral-400">
            No alternative names recorded.
          </div>
        )}
      </section>
    </div>
  );
}
